import type { FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { LayoutData, PageContent } from '@/types'

import { cms } from '@/lib/cms'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { getLayoutData, getPage } from '@/lib/content'

import RichText from '@/components/generals/rich-text'

type PrivacyProps = LayoutData & {
    page: PageContent | null
}

const Privacy: FC<PrivacyProps> = ({ settings, brands, page }): ReactNode => {
    return (
        <Layout settings={settings} brands={brands}>
            <Seo
                title={page?.title ?? 'Privacy policy'}
                siteName={settings?.site_name}
                description={page?.meta_description ?? undefined}
            />

            <section aria-labelledby='privacy-heading' className='bg-stone px-5 pb-16 pt-36 sm:px-8 sm:pb-20 sm:pt-44'>
                <div className='mx-auto max-w-3xl'>
                    <p className='eyebrow flex items-center gap-4 text-muted-foreground'>
                        <span aria-hidden='true' className='h-px w-10 bg-foreground/30' />
                        <span>Legal</span>
                    </p>

                    <h1 id='privacy-heading' className='display mt-6 text-4xl sm:text-5xl'>
                        {page
                            ? <span data-mdlfy-cms={cms('pages', page.id, 'title')}>{page.title}</span>
                            : <span>Privacy policy</span>}
                    </h1>

                    {page?.intro && (
                        <p className='mt-6 text-lg leading-relaxed text-muted-foreground'>
                            <span data-mdlfy-cms={cms('pages', page.id, 'intro')}>{page.intro}</span>
                        </p>
                    )}
                </div>
            </section>

            <section className='bg-background px-5 py-16 sm:px-8 sm:py-24'>
                <div className='mx-auto max-w-3xl'>
                    {page?.body
                        ? <RichText html={page.body} className='text-[1.05rem] leading-relaxed text-foreground/80 [&_h2]:text-xl [&_h2]:uppercase [&_h2]:tracking-[0.12em]' />
                        : <p className='text-muted-foreground'>Our privacy policy is being updated. Please check back soon.</p>}
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<PrivacyProps> = async () => {
    try {
        const [layout, page] = await Promise.all([getLayoutData(), getPage('privacy')])

        return { props: { ...layout, page }, revalidate: 60 }
    }
    catch (error) {
        console.error('Privacy page content could not be loaded:', error instanceof Error ? error.message : error)

        return { props: { settings: null, brands: [], page: null }, revalidate: 10 }
    }
}

export default Privacy
